/* Sticky add to cart, on phones and desktop (2026-10-08; it used to be phone-only). Mirror Dawn's live form: the bar's button
   clicks Dawn's own Add to cart button, which preserves variant, quantity and cart handling. The bar shows once that button
   has scrolled up out of view, and steps aside while the shopper types, while a drawer, modal or dialog is open, and near
   the footer. */
if (!customElements.get('hk-sticky-product')) {
  customElements.define('hk-sticky-product', class extends HTMLElement {
    connectedCallback() {
      if (!this.product) {
        const product = this.closest('product-info');
        // Dawn's quick-add modal injects the whole product-info, this block included (and marks it data-original-section).
        // Its own buy button is in view there, and a bar moved out of the modal would outlive it: it stays hidden in place.
        if (!product || product.dataset.originalSection || this.closest('quick-add-modal') || !this.querySelector('[data-sticky-add]')) return;
        this.product = product;
        // position:fixed anchors to the nearest ancestor with a transform, filter or will-change instead of to the screen.
        // "Reveal sections on scroll" leaves a transform on .product__info-wrapper (animate--slide-in), which would pin the
        // bar to the product column. As a direct child of <body> it has no such ancestor. Right after <main>, so its button
        // comes after the page content and before the footer in the tab order.
        // A bar left behind by an earlier render of this section (theme editor reload, a product swap that re-renders
        // product-info) goes first, so there is only ever one per section.
        for (const old of document.querySelectorAll('body > hk-sticky-product')) {
          if (old !== this && old.dataset.section === this.dataset.section) old.remove();
        }
        const main = document.querySelector('body > main');
        // Moving it runs connectedCallback again; this.product is set by then, so that call wires everything up.
        if (main) main.after(this); else document.body.append(this);
        return;
      }
      this.action = this.querySelector('[data-sticky-add]');
      this.queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
      };
      if (!this.action.id) this.action.id = `hk-sticky-add-${this.dataset.section}`;
      this.add = () => {
        const primary = this.primary();
        if (primary && !primary.disabled && primary.getAttribute('aria-disabled') !== 'true') {
          this.fromSticky = true;
          // Tell "Buy more, save more" (assets/houndkeep-qty-offer.js) where the click came from, so focus comes back here.
          primary.dataset.hkFromSticky = this.action.id;
          primary.click();
          delete primary.dataset.hkFromSticky;
          this.queue();
        }
      };
      this.action.addEventListener('click', this.add);
      this.controller = new AbortController();
      const options = { signal: this.controller.signal, passive: true };
      window.addEventListener('scroll', this.queue, options);
      window.addEventListener('resize', this.queue, options);
      document.addEventListener('focusin', this.queue, options);
      document.addEventListener('focusout', this.queue, options);
      this.product.addEventListener('change', this.queue, options);
      window.visualViewport?.addEventListener('resize', this.queue, options);
      // Theme editor: a re-rendered or removed section leaves this moved bar behind; sync() removes it once its product-info is gone.
      document.addEventListener('shopify:section:load', this.queue, options);
      document.addEventListener('shopify:section:unload', this.queue, options);
      // The bar's own updates are skipped: should it ever be wired up inside product-info, they would feed back into this
      // observer and loop (setting `hidden` writes the attribute even when the value stays the same).
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.queue();
      });
      this.observer.observe(this.product, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden', 'open'] });
      this.overlayObserver = new MutationObserver(() => this.sync());
      this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.queue();
    }

    primary() { return this.product.querySelector(`#ProductSubmitButton-${CSS.escape(this.dataset.section)}`); }

    sync() {
      if (!this.product.isConnected) { this.remove(); return; }
      const primary = this.primary();
      if (!primary) { this.hidden = true; return; }
      const priceContainer = this.product.querySelector(`#price-${CSS.escape(this.dataset.section)}`);
      const price = priceContainer?.querySelector('.price--on-sale .price-item--sale') || priceContainer?.querySelector('.price__regular .price-item--regular');
      const label = primary.querySelector('span')?.textContent.trim() || primary.textContent.trim();
      const disabled = primary.disabled || primary.getAttribute('aria-disabled') === 'true';
      // aria-disabled, not the disabled property: a focused button that becomes disabled drops the keyboard focus to <body>
      // (Dawn disables Add to cart while it adds), and a failed add would leave it there. add() already ignores clicks
      // while Dawn's button is busy, and Dawn styles .button[aria-disabled=true] like :disabled.
      this.action.setAttribute('aria-disabled', String(disabled));
      this.action.setAttribute('aria-busy', String(primary.classList.contains('loading')));
      if (this.action.textContent !== label) this.action.textContent = label;
      let priceText = priceContainer?.classList.contains('hidden') ? '' : (price?.textContent.trim() || '');
      let variant;
      try { variant = JSON.parse(this.product.querySelector('[data-selected-variant]')?.textContent || 'null'); } catch { variant = null; }
      let variantText = variant && variant.title !== 'Default Title' ? variant.title : '';
      // "Buy 2 / Buy 3" chosen: the bar adds those items, so it shows that total and "Buy 2" instead of one item's price
      // and Dawn's variant (review 2026-10-06). Both texts come from the block itself.
      const multi = this.product.querySelector('hk-qty-offer.is-multi [data-hk-option].is-active');
      if (multi) {
        priceText = multi.querySelector('[data-hk-now]')?.textContent.trim() || priceText;
        variantText = multi.querySelector('.hk-qty__name strong')?.textContent.trim() || variantText;
      }
      const priceOutput = this.querySelector('[data-sticky-price]');
      if (priceOutput.textContent !== priceText) priceOutput.textContent = priceText;
      const variantOutput = this.querySelector('[data-sticky-variant]');
      if (variantOutput.textContent !== variantText) variantOutput.textContent = variantText;
      // The thumbnail follows the selected variant's own photo; a variant without one keeps the current image.
      const thumbWrap = this.querySelector('.hk-sticky-product__thumb');
      const media = variant && variant.featured_media;
      const mediaSrc = media && media.preview_image && media.preview_image.src;
      if (thumbWrap && mediaSrc && thumbWrap.dataset.mediaId !== String(media.id)) {
        const img = thumbWrap.querySelector('img');
        if (img) {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = mediaSrc + (mediaSrc.includes('?') ? '&' : '?') + 'width=120';
          thumbWrap.dataset.mediaId = String(media.id);
        }
      }
      const sourceError = this.product.querySelector('.product-form__error-message-wrapper');
      // A failed "Buy 2/3" add writes its message in the block above the buy buttons, off-screen when the bar shows.
      // Show only the error of the mode that is chosen now: neither message is cleared when the shopper switches between
      // Buy 1 and Buy 2/3, so the other one can be stale (review 2026-10-06).
      const qtyError = this.product.querySelector('hk-qty-offer [data-hk-error]');
      const errorText = multi
        ? (qtyError && !qtyError.hidden ? qtyError.textContent.trim() : '')
        : (sourceError && !sourceError.hidden ? sourceError.querySelector('.product-form__error-message')?.textContent.trim() || '' : '');
      const errorOutput = this.querySelector('[data-sticky-error]');
      if (errorOutput.textContent !== errorText) errorOutput.textContent = errorText;
      errorOutput.hidden = !errorText;
      const editing = document.activeElement?.matches('input:not([type=radio]):not([type=checkbox]), textarea, select, [contenteditable=true]');
      const drawerOpen = document.body.classList.contains('overflow-hidden');
      // Dawn locks page scroll with overflow-hidden (cart drawer, modals, search) or overflow-hidden-mobile/-tablet/-desktop
      // (menu drawer, country/language list). Any of them means something is open over the page, so the bar steps aside.
      const scrollLocked = /(^|\s)overflow-hidden(-\w+)?(\s|$)/.test(document.body.className);
      const overlay = scrollLocked || document.querySelector('cart-drawer.active, dialog[open], .shopify-pc__banner__dialog');
      const footer = document.querySelector('footer');
      const footerVisible = footer && footer.getBoundingClientRect().top < innerHeight - 80;
      // Keyboard focus on the bar keeps it on screen while the page scrolls under it (arrow keys, Page Up/Down): hiding it
      // would drop that focus to <body>. A mouse click focuses the button too, but only keyboard focus is :focus-visible.
      let keyboardHere = false;
      try { keyboardHere = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible'); } catch { /* no :focus-visible: as before */ }
      this.hidden = !!editing || !!overlay || (!keyboardHere && (primary.getBoundingClientRect().bottom > 0 || !!footerVisible));
      if (this.wasDrawerOpen && !drawerOpen && this.fromSticky) {
        if (!this.hidden) this.action.focus({ preventScroll: true });
        this.fromSticky = false;
      }
      this.wasDrawerOpen = drawerOpen;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.overlayObserver?.disconnect();
      this.action?.removeEventListener('click', this.add);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0; // a bar that is connected again must be able to queue a new frame
    }
  });
}
